export type GameLanguage = 'en' | 'pt-BR' | 'fr' | 'it' | 'de' | 'zh' | 'ru';

export interface TranslationStrings {
  // Main Menu
  mainMenu: string;
  play: string;
  settings: string;
  towerInfo: string;
  gameInfo: string;
  about: string;
  credits: string;
  backToStartScreen: string;
  back: string;
  
  // Play Menu
  singlePlayer: string;
  coOp: string;
  pvp: string;
  maps: string;
  loadMap: string;
  editor: string;
  
  // Settings
  masterVolume: string;
  musicVolume: string;
  sfxVolume: string;
  quickSell: string;
  quickBuild: string;
  showInstructions: string;
  language: string;
  save: string;
  close: string;
  backToMainMenu: string;
  
  // Languages
  english: string;
  portuguese: string;
  french: string;
  italian: string;
  german: string;
  chinese: string;
  russian: string;
  
  // Common
  closeButton: string;
}

const translations: Record<GameLanguage, TranslationStrings> = {
  en: {
    mainMenu: 'Main Menu',
    play: 'Play',
    settings: 'Settings',
    towerInfo: 'Tower Info',
    gameInfo: 'Game Info',
    about: 'About',
    credits: 'Credits',
    backToStartScreen: 'Back to Start Screen',
    back: 'Back',
    singlePlayer: 'Single Player (local)',
    coOp: '4-Player Co-op (placeholder)',
    pvp: 'PVP 8-Player Survival (placeholder)',
    maps: 'Maps',
    loadMap: 'Load Map',
    editor: 'Editor',
    masterVolume: 'Master Volume:',
    musicVolume: 'Music Volume:',
    sfxVolume: 'SFX Volume:',
    quickSell: 'Quick Sell',
    quickBuild: 'Quick Build',
    showInstructions: 'Show instructions on start',
    language: 'Language:',
    save: 'Save',
    close: 'Close',
    backToMainMenu: 'Back to Main Menu',
    english: 'English',
    portuguese: 'Português (BR)',
    french: 'Français',
    italian: 'Italiano',
    german: 'Deutsch',
    chinese: '中文',
    russian: 'Русский',
    closeButton: 'Close',
  },
  'pt-BR': {
    mainMenu: 'Menu Principal',
    play: 'Jogar',
    settings: 'Configurações',
    towerInfo: 'Info das Torres',
    gameInfo: 'Info do Jogo',
    about: 'Sobre',
    credits: 'Créditos',
    backToStartScreen: 'Voltar à Tela Inicial',
    back: 'Voltar',
    singlePlayer: 'Jogador Único (local)',
    coOp: 'Co-op 4 Jogadores (placeholder)',
    pvp: 'PVP Sobrevivência 8 Jogadores (placeholder)',
    maps: 'Mapas',
    loadMap: 'Carregar Mapa',
    editor: 'Editor',
    masterVolume: 'Volume Master:',
    musicVolume: 'Volume da Música:',
    sfxVolume: 'Volume dos Efeitos:',
    quickSell: 'Venda Rápida',
    quickBuild: 'Construção Rápida',
    showInstructions: 'Mostrar instruções ao iniciar',
    language: 'Idioma:',
    save: 'Salvar',
    close: 'Fechar',
    backToMainMenu: 'Voltar ao Menu Principal',
    english: 'Inglês',
    portuguese: 'Português (BR)',
    french: 'Francês',
    italian: 'Italiano',
    german: 'Alemão',
    chinese: 'Chinês',
    russian: 'Russo',
    closeButton: 'Fechar',
  },
  fr: {
    mainMenu: 'Menu Principal',
    play: 'Jouer',
    settings: 'Paramètres',
    towerInfo: 'Info Tours',
    gameInfo: 'Info Jeu',
    about: 'À Propos',
    credits: 'Crédits',
    backToStartScreen: 'Retour à l\'Écran de Démarrage',
    back: 'Retour',
    singlePlayer: 'Un Joueur (local)',
    coOp: 'Co-op 4 Joueurs (placeholder)',
    pvp: 'PVP Survie 8 Joueurs (placeholder)',
    maps: 'Cartes',
    loadMap: 'Charger Carte',
    editor: 'Éditeur',
    masterVolume: 'Volume Principal:',
    musicVolume: 'Volume Musique:',
    sfxVolume: 'Volume Effets:',
    quickSell: 'Vente Rapide',
    quickBuild: 'Construction Rapide',
    showInstructions: 'Afficher instructions au démarrage',
    language: 'Langue:',
    save: 'Enregistrer',
    close: 'Fermer',
    backToMainMenu: 'Retour au Menu Principal',
    english: 'Anglais',
    portuguese: 'Portugais (BR)',
    french: 'Français',
    italian: 'Italien',
    german: 'Allemand',
    chinese: 'Chinois',
    russian: 'Russe',
    closeButton: 'Fermer',
  },
  it: {
    mainMenu: 'Menu Principale',
    play: 'Gioca',
    settings: 'Impostazioni',
    towerInfo: 'Info Torri',
    gameInfo: 'Info Gioco',
    about: 'Informazioni',
    credits: 'Crediti',
    backToStartScreen: 'Torna alla Schermata Iniziale',
    back: 'Indietro',
    singlePlayer: 'Giocatore Singolo (locale)',
    coOp: 'Co-op 4 Giocatori (placeholder)',
    pvp: 'PVP Sopravvivenza 8 Giocatori (placeholder)',
    maps: 'Mappe',
    loadMap: 'Carica Mappa',
    editor: 'Editor',
    masterVolume: 'Volume Master:',
    musicVolume: 'Volume Musica:',
    sfxVolume: 'Volume Effetti:',
    quickSell: 'Vendita Rapida',
    quickBuild: 'Costruzione Rapida',
    showInstructions: 'Mostra istruzioni all\'avvio',
    language: 'Lingua:',
    save: 'Salva',
    close: 'Chiudi',
    backToMainMenu: 'Torna al Menu Principale',
    english: 'Inglese',
    portuguese: 'Portoghese (BR)',
    french: 'Francese',
    italian: 'Italiano',
    german: 'Tedesco',
    chinese: 'Cinese',
    russian: 'Russo',
    closeButton: 'Chiudi',
  },
  de: {
    mainMenu: 'Hauptmenü',
    play: 'Spielen',
    settings: 'Einstellungen',
    towerInfo: 'Turm-Info',
    gameInfo: 'Spiel-Info',
    about: 'Über',
    credits: 'Credits',
    backToStartScreen: 'Zurück zum Startbildschirm',
    back: 'Zurück',
    singlePlayer: 'Einzelspieler (lokal)',
    coOp: '4-Spieler Co-op (placeholder)',
    pvp: 'PVP Überleben 8 Spieler (placeholder)',
    maps: 'Karten',
    loadMap: 'Karte Laden',
    editor: 'Editor',
    masterVolume: 'Hauptlautstärke:',
    musicVolume: 'Musiklautstärke:',
    sfxVolume: 'Effektlautstärke:',
    quickSell: 'Schnellverkauf',
    quickBuild: 'Schnellbau',
    showInstructions: 'Anweisungen beim Start anzeigen',
    language: 'Sprache:',
    save: 'Speichern',
    close: 'Schließen',
    backToMainMenu: 'Zurück zum Hauptmenü',
    english: 'Englisch',
    portuguese: 'Portugiesisch (BR)',
    french: 'Französisch',
    italian: 'Italienisch',
    german: 'Deutsch',
    chinese: 'Chinesisch',
    russian: 'Russisch',
    closeButton: 'Schließen',
  },
  zh: {
    mainMenu: '主菜单',
    play: '游戏',
    settings: '设置',
    towerInfo: '塔信息',
    gameInfo: '游戏信息',
    about: '关于',
    credits: '制作人员',
    backToStartScreen: '返回开始画面',
    back: '返回',
    singlePlayer: '单人游戏 (本地)',
    coOp: '4人合作 (占位符)',
    pvp: '8人PVP生存 (占位符)',
    maps: '地图',
    loadMap: '加载地图',
    editor: '编辑器',
    masterVolume: '主音量:',
    musicVolume: '音乐音量:',
    sfxVolume: '音效音量:',
    quickSell: '快速出售',
    quickBuild: '快速建造',
    showInstructions: '启动时显示说明',
    language: '语言:',
    save: '保存',
    close: '关闭',
    backToMainMenu: '返回主菜单',
    english: '英语',
    portuguese: '葡萄牙语 (BR)',
    french: '法语',
    italian: '意大利语',
    german: '德语',
    chinese: '中文',
    russian: '俄语',
    closeButton: '关闭',
  },
  ru: {
    mainMenu: 'Главное Меню',
    play: 'Играть',
    settings: 'Настройки',
    towerInfo: 'Инфо Башен',
    gameInfo: 'Инфо Игры',
    about: 'О Игре',
    credits: 'Благодарности',
    backToStartScreen: 'Вернуться к Начальному Экрану',
    back: 'Назад',
    singlePlayer: 'Одиночная Игра (локально)',
    coOp: 'Кооп 4 Игрока (заглушка)',
    pvp: 'PVP Выживание 8 Игроков (заглушка)',
    maps: 'Карты',
    loadMap: 'Загрузить Карту',
    editor: 'Редактор',
    masterVolume: 'Общая Громкость:',
    musicVolume: 'Громкость Музыки:',
    sfxVolume: 'Громкость Эффектов:',
    quickSell: 'Быстрая Продажа',
    quickBuild: 'Быстрое Строительство',
    showInstructions: 'Показывать инструкции при запуске',
    language: 'Язык:',
    save: 'Сохранить',
    close: 'Закрыть',
    backToMainMenu: 'Вернуться в Главное Меню',
    english: 'Английский',
    portuguese: 'Португальский (BR)',
    french: 'Французский',
    italian: 'Итальянский',
    german: 'Немецкий',
    chinese: 'Китайский',
    russian: 'Русский',
    closeButton: 'Закрыть',
  },
};

export function getTranslation(language: GameLanguage, key: keyof TranslationStrings): string {
  return translations[language]?.[key] || translations.en[key] || key;
}

export function getAllTranslations(language: GameLanguage): TranslationStrings {
  return translations[language] || translations.en;
}

